// ── Motion Primitives ─────────────────────────────────────────────────
// Pixelgenau aus design_handoff_red_webapp/source/red-motion.jsx.
// Respektiert prefers-reduced-motion (Reveal aus, Ticker langsamer).
// useLayoutEffect für robusten Setup (auch ohne Paint).

import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { F, MONO_FEAT, withAlpha, EASE } from '../../styles/theme';
import { smoothPath } from './Charts';

// Eine Bewegungs-Handschrift: dieselbe Spring-Kurve wie theme.EASE / index.css.
const SPRING = EASE;

function prefersReduced() {
  return typeof matchMedia !== 'undefined'
    && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// ── useInView — feuert einmalig wenn Element ins Viewport scrollt ──
export function useInView(threshold = 0.18, rootMargin = '0px 0px -8% 0px') {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersReduced()) { setInView(true); return; }
    let done = false;
    const reveal = () => { if (!done) { done = true; setInView(true); } };
    const timer = setTimeout(reveal, 650);
    let io;
    try {
      io = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          if (e.isIntersecting || e.intersectionRatio > 0) {
            reveal();
            io.disconnect();
          }
        });
      }, { threshold, rootMargin });
      io.observe(el);
    } catch {
      reveal();
    }
    return () => { clearTimeout(timer); if (io) io.disconnect(); };
  }, [threshold, rootMargin]);
  return [ref, inView];
}

// ── useCountUp — easeOutQuart, animiert vom aktuellen Wert zum target ──
// Wichtig: target kann sich nach dem Mount ändern (Daten/Preise laden erst
// asynchron). Daher KEINE einmalige started-Sperre — bei jeder target-Änderung
// wird vom zuletzt angezeigten Wert auf das neue target animiert. Sonst bliebe
// die Zahl auf dem Initialwert (0) hängen, wenn die echten Werte später kommen.
export function useCountUp(target, { duration = 1400, run = true, delay = 0 } = {}) {
  const [val, setVal] = useState(0);
  const valRef = useRef(0);
  useLayoutEffect(() => {
    if (!run) return;
    if (prefersReduced()) { valRef.current = target; setVal(target); return; }
    const from = valRef.current;
    if (from === target) return;
    let raf;
    const startAt = performance.now() + delay;
    const tick = (now) => {
      if (now < startAt) { raf = requestAnimationFrame(tick); return; }
      const t = Math.min(1, (now - startAt) / duration);
      const e = 1 - Math.pow(1 - t, 4); // easeOutQuart
      const next = from + (target - from) * e;
      valRef.current = next;
      setVal(next);
      if (t < 1) raf = requestAnimationFrame(tick);
      else { valRef.current = target; setVal(target); }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [run, target, duration, delay]);
  return val;
}

// ── Reveal — gestaffelte Einblendung (opacity + translateY) on scroll-in ──
export function Reveal({ children, delay = 0, y = 20, scale, dur = 720, style, className }) {
  const [ref, inView] = useInView(0.14);
  const tf = inView ? 'none' : `translateY(${y}px)${scale ? ` scale(${scale})` : ''}`;
  return (
    <div ref={ref} className={className} style={{
      opacity: inView ? 1 : 0,
      transform: tf,
      willChange: 'opacity, transform',
      transition: `opacity ${dur}ms ${SPRING} ${delay}ms, transform ${dur}ms ${SPRING} ${delay}ms`,
      ...style,
    }}>
      {children}
    </div>
  );
}

// ── CountTo — kinetische Zahl (Space Grotesk tabular-nums) ──
export function CountTo({ value, format, run = true, duration = 1500, delay = 0, style }) {
  const v = useCountUp(value, { duration, run, delay });
  const text = format
    ? format(v)
    : Math.round(v).toLocaleString('de-DE');
  return <span style={{ ...MONO_FEAT, ...style }}>{text}</span>;
}

// ── Aurora — subtile Punkt-Raster-Textur + warmer Wash (kein Glow!) ──
export function Aurora({ accent, intensity = 1, style }) {
  const { T, isDark } = useTheme();
  const dot = isDark ? withAlpha('#FFFFFF', 0.04) : withAlpha('#0C0C0F', 0.05);
  const accentColor = accent || T.signal || T.red;
  return (
    <div aria-hidden="true" style={{
      position: 'absolute', inset: 0,
      overflow: 'hidden', pointerEvents: 'none',
      ...style,
    }}>
      <div style={{
        position: 'absolute', top: '-30%', left: '-10%',
        width: '70%', height: '120%',
        background: `radial-gradient(ellipse 60% 55% at 30% 35%, ${withAlpha(accentColor, (isDark ? 0.10 : 0.06) * intensity)} 0%, transparent 70%)`,
      }} />
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `radial-gradient(${dot} 1px, transparent 1px)`,
        backgroundSize: '22px 22px', backgroundPosition: '-1px -1px',
        maskImage: 'linear-gradient(160deg, #000 0%, transparent 75%)',
        WebkitMaskImage: 'linear-gradient(160deg, #000 0%, transparent 75%)',
      }} />
    </div>
  );
}

// ── AnimatedArea — Hero-Chart, zeichnet Linie animiert ──
export function AnimatedArea({ data, height = 240, color, months, app = false }) {
  const { T } = useTheme();
  const [hostRef, inView] = useInView(0.2);
  const sizeRef = useRef(null);
  const [w, setW] = useState(720);
  useEffect(() => {
    if (!sizeRef.current) return;
    const ro = new ResizeObserver(es => {
      for (const e of es) setW(Math.max(120, e.contentRect.width));
    });
    ro.observe(sizeRef.current);
    return () => ro.disconnect();
  }, []);
  const stroke = color || T.signal || T.red;
  if (!data || data.length < 2) return <div ref={hostRef} style={{ height }} />;
  const min = Math.min(...data), max = Math.max(...data), range = max - min || 1;
  const padB = months ? 26 : 6;
  const h = height - padB;
  // App-Variante (Ledger-Stil): stark glätten → die Linie FLIESST, statt jeden
  // Roh-Tick zu zacken. Gleichmäßig auf ~22 Stützpunkte herunterrechnen; die
  // Extreme bleiben über min/max der vollen Daten erhalten (Skala stimmt).
  const plot = (app && data.length > 24)
    ? Array.from({ length: 22 }, (_, i) => data[Math.round(i * (data.length - 1) / 21)])
    : data;
  const step = w / (plot.length - 1);
  const pts = plot.map((v, i) => [i * step, h - ((v - min) / range) * (h - 14) - 7]);
  // Weiche Kurve statt Polyline — derselbe bewährte smoothPath wie BigAreaChart
  // (Charts.js), damit Hero-Chart und Asset-Chart dieselbe seidige Sprache sprechen.
  const line = smoothPath(pts.map(([x, y]) => ({ x, y })));
  const area = line + ` L ${w} ${h} L 0 ${h} Z`;
  const last = pts[pts.length - 1];
  const uid = 'aa' + Math.abs((stroke.charCodeAt(1) || 1) * 7 + data.length);
  const gridY = [0, 0.25, 0.5, 0.75, 1].map(f => h * f);
  return (
    <div ref={hostRef} data-noswipe style={{ width: '100%' }}>
      <div ref={sizeRef} style={{ width: '100%' }}>
        <svg width={w} height={height} style={{ display: 'block', overflow: 'visible' }}>
          <defs>
            <linearGradient id={uid + 'f'} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={stroke} stopOpacity={app ? (T.isDark ? 0.30 : 0.20) : (T.isDark ? 0.16 : 0.12)} />
              <stop offset="100%" stopColor={stroke} stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* Gitterlinien nur in der Webapp; die App-Variante (Ledger-Stil) ist clean. */}
          {!app && gridY.map((y, i) => (
            <line key={i} x1="0" y1={y} x2={w} y2={y}
              stroke={T.line} strokeWidth="1"
              strokeDasharray={i === gridY.length - 1 ? '0' : '2 5'}
              opacity={i === gridY.length - 1 ? 0.9 : 0.32}
            />
          ))}
          <path d={area} fill={`url(#${uid}f)`} style={{
            opacity: inView ? 1 : 0,
            transition: `opacity .9s ease ${inView ? 400 : 0}ms`,
          }} />
          {/* App-Variante (Ledger-Stil): KEIN Blur-Halo unter der Linie — der
             wirkte fuzzy/„beschissen". Nur die saubere glatte Linie + Fläche. */}
          <path d={line} fill="none" stroke={stroke} strokeWidth={app ? 2.4 : 1.6}
            strokeLinecap="round" strokeLinejoin="round"
            pathLength="1" strokeDasharray="1"
            style={{
              strokeDashoffset: inView ? 0 : 1,
              transition: `stroke-dashoffset 1.5s ${SPRING}`,
            }}
          />
          {/* Endpunkt: dezenter dünner Ring (kein Blur-Klecks) — wie im Ziel-Mock. */}
          {app && (
            <circle cx={last[0]} cy={last[1]} r="8" fill="none" stroke={stroke} strokeWidth="2"
              style={{ opacity: inView ? 0.25 : 0, transition: 'opacity .5s ease 1.3s' }} />
          )}
          <circle cx={last[0]} cy={last[1]} r={app ? 4 : 4.5}
            fill={stroke} stroke={app ? (T.isDark ? '#14151B' : '#FFFFFF') : T.surface} strokeWidth={app ? 2 : 2.5}
            style={{ opacity: inView ? 1 : 0, transition: 'opacity .4s ease 1.3s' }}
          />
          {months && months.map((m, i) => (
            <text key={i}
              x={(i / (months.length - 1)) * w} y={height - 6}
              textAnchor={i === 0 ? 'start' : i === months.length - 1 ? 'end' : 'middle'}
              fontFamily={F.num} fontSize="10.5" fill={T.mute}
              style={{
                opacity: inView ? 1 : 0,
                transition: `opacity .6s ease ${700 + i * 80}ms`,
              }}
            >{m}</text>
          ))}
        </svg>
      </div>
    </div>
  );
}

// ── Ticker — Marquee, 38s linear, an Rändern weich ausmaskiert ──
export function Ticker({ items }) {
  const { T } = useTheme();
  const row = (key) => (
    <div key={key} style={{
      display: 'flex', alignItems: 'center', gap: 28,
      paddingRight: 28, flexShrink: 0,
    }}>
      {items.map((it, i) => (
        <span key={i} style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          whiteSpace: 'nowrap',
        }}>
          <span style={{
            ...MONO_FEAT,
            fontWeight: 600, fontSize: 12.5,
            color: T.sub, letterSpacing: '0.02em',
          }}>{it.sym}</span>
          <span style={{
            ...MONO_FEAT,
            fontWeight: 600, fontSize: 12.5,
            color: T.text,
          }}>{it.price}</span>
          <span style={{
            ...MONO_FEAT,
            fontWeight: 600, fontSize: 11.5,
            color: it.chg >= 0 ? (T.gain || T.green) : (T.loss || T.red),
          }}>
            {it.chg >= 0 ? '+' : '−'}{Math.abs(it.chg).toFixed(2)}%
          </span>
        </span>
      ))}
    </div>
  );
  return (
    <div style={{
      overflow: 'hidden', position: 'relative',
      maskImage: 'linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)',
      WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)',
    }}>
      <div style={{
        display: 'flex', width: 'max-content',
        animation: 'redTickerScroll 38s linear infinite',
      }}>
        {row('a')}{row('b')}
      </div>
      <style>{`
        @keyframes redTickerScroll {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="redTickerScroll"] { animation-duration: 120s !important; }
        }
      `}</style>
    </div>
  );
}

// ── Sparkline — kleine Linie für Holdings/Markets ──
export function Sparkline({ data, color, width = 64, height = 26, sw = 1.5, fillOpacity = 0 }) {
  if (!data || data.length < 2) return <div style={{ width, height }} />;
  const min = Math.min(...data), max = Math.max(...data), range = max - min || 1;
  const step = width / (data.length - 1);
  const pts = data.map((v, i) => [i * step, height - ((v - min) / range) * (height - 2) - 1]);
  const line = pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
  const area = line + ` L ${width} ${height} L 0 ${height} Z`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}
      style={{ display: 'block', overflow: 'visible' }} aria-hidden="true">
      {fillOpacity > 0 && <path d={area} fill={color} opacity={fillOpacity} />}
      <path d={line} fill="none" stroke={color} strokeWidth={sw}
        strokeLinecap="round" strokeLinejoin="round"
        vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
